import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from 'store/store';
import { intakeFormSettingsAction } from 'reduxStateSlice/intakeFormSettings';
import { ModuleTypes } from 'types/appState';
import { appStateSettingsAction } from 'reduxStateSlice/appState';
import PatientEntry from 'components/patient/patientEntry';
import Alert from '@mui/material/Alert';
import Spinner from 'components/spinner/Spinner';
import MetaTag from 'components/meta';
import { clearAppLocalStorage } from 'utils/helper';
import ConsentFormApp from 'components/consentForm';
import ConsentFormStartScreen from 'components/consentForm/consentFormStartScreen';
import MedicalHistoryStartScreen from 'components/medicalHistory/medicalHistoryStart';
import PharmacyMedicationsStartScreen from 'components/pharmacyMedications/PharmacyMedicationsStart';
import NoPodiatry from 'components/nonPodiatry/reasonforVisit';
import ExistingPatient from 'components/existingPatient/reasonforVisitExistingUser';
import ReasonForVisit from 'components/podiatry/reasonforVisit';
import DisclaimerMessage from 'components/disclaimerMessage';
import AppointmentConfirmation from 'components/appointmentConfirmation';
import { auth0M2MAccessToken, isIpadBaseIntake, isTabletBaseIntake } from 'env';
import CommuniqueByPatientInfo from 'components/communiqueByPatientInfo';
import Tempo from 'components/tempo';
import RtmEnrollment from 'components/rtmEnrollment';
import RtmCheckIn from 'components/rtmCheckIn';
import ConsentFormComplete from 'components/consentForm/consentFormComplete';
import isNil from 'lodash/isNil';
import { useGetIntakeFormSettings } from 'hooks/useApi';
import { hasToken, setM2MHeaders } from 'utils/auth';

function Home() {
  const appState = useSelector((state: RootState) => state.appStateSettings);
  const [errorMessage, setErrorMessage] = useState('');
  const [isM2MReady, setIsM2MReady] = useState(false);
  const {
    data: intakeFormSettings,
  } = useGetIntakeFormSettings(isM2MReady);
  const dispatch = useDispatch();

  useEffect(() => {
    if (!isNil(intakeFormSettings)) {
      dispatch(intakeFormSettingsAction(intakeFormSettings));
      dispatch(
        appStateSettingsAction({
          name: /true/i.test(isIpadBaseIntake) ? ModuleTypes.communiqueByPatientInfo : ModuleTypes.disclaimerMessage,
          page: null,
        }),
      );
    }
  }, [intakeFormSettings]);

  useEffect(() => {
    setM2MHeaders(auth0M2MAccessToken ?? '');
    clearAppLocalStorage();
    setIsM2MReady(hasToken());
  }, [auth0M2MAccessToken]);

  const isTabletOrPccConsent = isTabletBaseIntake
    || Boolean(appState.pccConsentWorkflowId)
    || Boolean(appState.pendingTarget)
    || Boolean(appState.appointmentToken);

  return (
    <>
      <MetaTag title="Welcome to Populate Intake App" />
      {ModuleTypes.communiqueByPatientInfo === appState.name && <CommuniqueByPatientInfo />}
      {ModuleTypes.disclaimerMessage === appState.name && <DisclaimerMessage />}
      {ModuleTypes.appointmentConfirmation === appState.name && <AppointmentConfirmation />}
      {ModuleTypes.patientList === appState.name && <PatientEntry />}
      {ModuleTypes.podiatry === appState.name && <ReasonForVisit />}
      {ModuleTypes.nonPodiatry === appState.name && <NoPodiatry />}
      {ModuleTypes.existingPatient === appState.name && <ExistingPatient />}
      {ModuleTypes.completed === appState.name && <ConsentFormComplete />}
      {ModuleTypes.medicalHistory === appState.name && (
        <MedicalHistoryStartScreen />
      )}
      {ModuleTypes.pharmacyMedications === appState.name && (
        <PharmacyMedicationsStartScreen />
      )}
      {ModuleTypes.consentForm === appState.name && (
        isTabletOrPccConsent ? <ConsentFormApp /> : <ConsentFormStartScreen />
      )}
      {ModuleTypes.tempo === appState.name && <Tempo />}
      {ModuleTypes.rtmEnrollment === appState.name && <RtmEnrollment />}
      {ModuleTypes.rtmCheckIn === appState.name && <RtmCheckIn />}
      {errorMessage ? (
        <div className="flex justify-center items-center, h-[90vh]">
          <Alert severity="error" className="items-center bg-transparent">
            {errorMessage}
          </Alert>
        </div>
      ) : (
        !appState.name && (
          <div className="flex justify-center items-center, h-[90vh]">
            <Spinner />
          </div>
        )
      )}
    </>
  );
}

export default Home;
